import { Typography, Box, ButtonGroup, Button, Grid } from '@mui/material'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import SelectionDialog, {
    IItemList,
} from 'components/Sell/OrderLookup/FilterSection/SelectionDialog'
import {
    MapPaymentIcon,
    mapPaymentTypeName,
} from 'components/MapPaymentIcon/mapPaymentIcon'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import MonetaryFormat from 'components/MonetaryFormat/MonetaryFormat'
import { CalculatorDialog } from '../../../CalculationInput/CalculatorDialog'
import useSplitPaymentContext, { PaymentFormModel } from './SplitPaymentContext'
import { isUndefined } from 'lodash-es'
import ArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import Decimal from 'decimal.js'
import styles from '../../paymentDialog.style'
import usePaymentContext from '../../hook/usePaymentContext'
import PaymentSettingSelectorDialog from 'components/QuickSaleMobile/Dialog/PaymentDialogMobile/PaymentSettingSelectorDialog'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'

interface IPaymentTypeProps {
    payment: PaymentFormModel
    index: number
    onUpdatePayment: (payment: PaymentFormModel) => void
    onDelete: () => void
    disableRemove: boolean
    disableCharge: boolean
}

export function PaymentTypeInput({
    index,
    payment,
    onUpdatePayment,
    onDelete,
    disableRemove,
    disableCharge,
}: IPaymentTypeProps) {
    const { t } = useTranslation()
    const {
        quickButtons,
        paymentTypeLists: paymentSettings,
    } = usePaymentContext()
    const inputQuickBottons = useMemo(() => {
        const newQuickBottons = [...quickButtons]
        newQuickBottons[0] = 0
        return newQuickBottons.filter((a) => a !== 0)
    }, [quickButtons])
    const onSelectPaymentType = useCallback(
        (paymentSettingId: string) => {
            onUpdatePayment({
                ...payment,
                paymentSettingID: paymentSettingId,
                paymentType: paymentSettings.find(
                    (a) => a.paymentSettingID === paymentSettingId,
                )?.paymentTypeID!,
            })

            setOpenPaymentTypeSelection(false)
        },
        [onUpdatePayment, payment, paymentSettings],
    )
    const onUpdateAmount = useCallback(
        (amount: Decimal) => {
            onUpdatePayment({
                ...payment,
                expectAmount: amount,
                isManual: true,
            })
            setOpenInput(false)
        },
        [onUpdatePayment, payment],
    )
    const { onClickCharging } = useSplitPaymentContext()

    const [openPaymentTypeSelection, setOpenPaymentTypeSelection] = useState(
        false,
    )

    const [openInput, setOpenInput] = useState(false)
    const paymentTypeList = useMemo(() => {
        let list: IItemList<string>[] =
            paymentSettings?.map((l) => {
                return {
                    label: l.name ?? '',
                    value: l.paymentSettingID!,
                    isPin: false,
                    disabled: l.disabled(payment.expectAmount.toNumber()),
                    icon: (
                        <MapPaymentIcon
                            customLogoUrl={l.customLogoUrl!}
                            paymentTypeId={l.paymentTypeID!}
                        />
                    ),
                }
            }) ?? []

        return list
    }, [payment.expectAmount, paymentSettings])
    const disableEdit =
        !isUndefined(payment.PaymentID) || Boolean(payment.giftcard!)
    const { isMobile } = useScreenSize()
    return (
        <>
            <Grid
                container
                direction={'row'}
                spacing={2}
                alignContent={'center'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Grid item xs={10} sm={9} md lg order={{ xs: 1 }}>
                    <ButtonGroup size={'large'} variant="outlined" fullWidth>
                        <Button
                            sx={styles.paymentInputButton}
                            disabled={disableEdit}
                            color="secondary"
                            onClick={() => {
                                if (isUndefined(payment.PaymentID))
                                    setOpenPaymentTypeSelection(true)
                            }}
                        >
                            <Box sx={styles.typeInputButton}>
                                {mapPaymentTypeName(
                                    payment.paymentType ?? 0,
                                    true,
                                    {
                                        paymentSetting: paymentSettings.find(
                                            (a) =>
                                                a.paymentSettingID ===
                                                payment.paymentSettingID!,
                                        ),
                                    },
                                )}
                            </Box>
                            <ArrowDownIcon />
                        </Button>
                        <Button
                            disabled={disableEdit}
                            color="secondary"
                            sx={styles.valueInputButton}
                            onClick={() => {
                                if (isUndefined(payment.PaymentID))
                                    setOpenInput(true)
                            }}
                        >
                            <MonetaryFormat
                                isOnlyString
                                value={payment.expectAmount.toNumber()}
                            />
                        </Button>
                    </ButtonGroup>
                </Grid>

                {!isUndefined(payment.PaymentID) ? (
                    <Grid item xs={2} sm={3} md lg order={{ xs: 2, md: 4 }}>
                        <Typography fontStyle={'italic'} color={'primary'}>
                            {t('finish.label.paid')}
                        </Typography>
                    </Grid>
                ) : (
                    <>
                        <Grid item xs order={{ xs: 3, sm: 3, md: 2 }}>
                            <Button
                                fullWidth
                                size="large"
                                color="primary"
                                disabled={disableCharge}
                                onClick={() => onClickCharging(payment, index)}
                            >
                                {t('finish.label.charge')}
                            </Button>
                        </Grid>
                        <Grid
                            item
                            xs={2}
                            sm={3}
                            md
                            lg
                            order={{ xs: 2, sm: 2, md: 3 }}
                        >
                            <Button
                                size="large"
                                disabled={
                                    !isUndefined(payment.PaymentID) ||
                                    disableRemove ||
                                    disableEdit
                                }
                                onClick={onDelete}
                                variant="text"
                            >
                                <DeleteForeverIcon fontSize="large" />
                            </Button>
                        </Grid>
                    </>
                )}
            </Grid>

            <Box pl={1} width={350}>
                {payment.giftcard && (
                    <Typography
                        variant="subtitle2"
                        color="primary"
                    >{`Gift Card: ${payment?.giftcard?.issueNo}`}</Typography>
                )}
            </Box>
            {/* Dialog */}
            {isMobile ? (
                openPaymentTypeSelection && (
                    <PaymentSettingSelectorDialog
                        paymentTypeSettings={paymentSettings}
                        onClose={() => setOpenPaymentTypeSelection(false)}
                        onSelectPaymentType={onSelectPaymentType}
                        expectAmount={payment.expectAmount.toNumber()}
                    />
                )
            ) : (
                <SelectionDialog
                    initSelectedItem={payment.paymentSettingID}
                    items={paymentTypeList}
                    open={openPaymentTypeSelection}
                    onClose={(t) => onSelectPaymentType(t!)}
                    title={t('finish.label.payment_type')}
                />
            )}

            <CalculatorDialog
                quickButtons={inputQuickBottons}
                initAmount={0}
                open={openInput}
                onClose={() => setOpenInput(false)}
                onConfirm={onUpdateAmount}
            />
        </>
    )
}
